/**
 * Navigation sur la grille d'une salle générée : champ de distances vers le joueur (parcours en largeur sur les
 * cellules marchables, recalculé quand le joueur change de cellule), pas de navigation des monstres et choix
 * des points d'apparition loin du joueur.
 */
import type { RoomGrid } from '../../data/schema';
import type { Rng } from '../rng';
import type { Vec2 } from '../types';
import { bfs, Bitset, CELL_H, CELL_W, CELLS, cellAt, centerOf, sideNeighbours } from './grid';

export interface NavField { cell: number; dist: Int16Array; }

// Empreinte au sol : disque vu en isométrie = ellipse de demi-axes r (horizontal) et r/2 (vertical), 9 points.
const FOOT: [number, number][] = [[0, 0], [1, 0], [-1, 0], [0, 0.5], [0, -0.5], [0.71, 0.35], [-0.71, 0.35], [0.71, -0.35], [-0.71, -0.35]];

/** Vrai si les 9 points de l'empreinte de rayon `radius` centrée en `pos` tombent sur des cellules marchables. */
export function footprintFits(walk: Bitset, pos: Vec2, radius: number): boolean {
  for (const [fx, fy] of FOOT) if (!walk.get(cellAt(pos.x + fx * radius, pos.y + fy * radius))) return false;
  return true;
}


const walkCache = new WeakMap<RoomGrid, Bitset>();
export function walkOf(grid: RoomGrid): Bitset {
  let b = walkCache.get(grid);
  if (!b) { b = new Bitset(CELLS, grid.walk); walkCache.set(grid, b); }
  return b;
}

/** Champ de distances vers `target`, réutilisé tant que la cellule cible ne change pas. */
export function navField(grid: RoomGrid, target: Vec2, prev: NavField | null): NavField | null {
  const cell = cellAt(target.x, target.y);
  if (cell < 0) return prev;
  if (prev && prev.cell === cell) return prev;
  return { cell, dist: bfs(walkOf(grid), cell) };
}

/**
 * Vrai si le segment a -> b est praticable : cellules marchables sous le centre (échantillons tous les 30 px), ou,
 * avec `radius` > 0, empreinte entière du corps (tous les 20 px). Sans l'empreinte, un monstre file tout droit
 * vers un coin d'obstacle que son corps ne peut pas franchir et reste bloqué contre lui.
 */
export function lineClear(grid: RoomGrid, a: Vec2, b: Vec2, radius = 0): boolean {
  const walk = walkOf(grid);
  const step = radius > 0 ? 20 : 30;
  const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / step));
  for (let k = 1; k <= n; k++) {
    const q = { x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n };
    if (radius > 0 ? !footprintFits(walk, q, radius) : !walk.get(cellAt(q.x, q.y))) return false;
  }
  return radius > 0 || walk.get(cellAt(a.x, a.y));
}

/**
 * Direction de marche (unitaire) de `from` vers `to` : en ligne droite si le passage est dégagé, sinon vers le
 * centre de la cellule voisine (par un côté) la plus proche de la cible dans le champ de distances.
 */
export function steer(grid: RoomGrid, field: NavField | null, from: Vec2, to: Vec2, radius = 0): Vec2 {
  const direct = (): Vec2 => { const dx = to.x - from.x, dy = to.y - from.y, d = Math.hypot(dx, dy) || 1; return { x: dx / d, y: dy / d }; };
  if (!field || lineClear(grid, from, to, radius)) return direct();
  const here = cellAt(from.x, from.y);
  if (here < 0) return direct();
  let best = -1, bestD = field.dist[here]! >= 0 ? field.dist[here]! : 32767;
  for (const k of sideNeighbours(here)) {
    const d = field.dist[k]!;
    if (d >= 0 && d < bestD) { bestD = d; best = k; }
  }
  if (best < 0) return direct();
  const c = centerOf(best);
  const dx = c.x - from.x, dy = c.y - from.y, d = Math.hypot(dx, dy) || 1;
  return { x: dx / d, y: dy / d };
}

/**
 * `n` points d'apparition : cellules marchables atteignables depuis le joueur, à une distance comprise entre
 * `min` et `max` px (repli : les plus lointaines), tirés avec le PRNG du monde, légèrement décalés du centre.
 */
export function pickSpawnPoints(grid: RoomGrid, rng: Rng, player: Vec2, n: number, min = 650, max = 1500): Vec2[] {
  const pc = cellAt(player.x, player.y);
  const dist = pc >= 0 ? bfs(walkOf(grid), pc) : null;
  const ok: number[] = [], far: { i: number; d: number }[] = [];
  for (let i = 0; i < CELLS; i++) {
    if (!dist || dist[i]! <= 0) continue;
    const c = centerOf(i);
    const d = Math.hypot(c.x - player.x, c.y - player.y);
    if (d >= min && d <= max) ok.push(i);
    far.push({ i, d });
  }
  let pool = ok;
  if (pool.length < n) pool = far.sort((a, b) => b.d - a.d || a.i - b.i).slice(0, Math.max(n * 3, 12)).map((f) => f.i);
  const out: Vec2[] = [];
  for (let k = 0; k < n && pool.length; k++) {
    const c = centerOf(pool[rng.int(0, pool.length - 1)]!);
    out.push({ x: c.x + (rng.next() - 0.5) * 0.3 * CELL_W, y: c.y + (rng.next() - 0.5) * 0.3 * CELL_H });
  }
  return out;
}
